// src/components/ChatSidebar.tsx
import React, { useCallback, useMemo, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { InputBase, List, ListItem, ListItemButton, styled, InputAdornment, alpha } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TypingTitle from "./typingtitle";
import SearchIcon from '@mui/icons-material/Search';
import ChangePassword from "./ChangePassword";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Импорты для drag-and-drop
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';

import dayjs from "dayjs";
import {
Box,
Button,
Dialog,
DialogContent,
DialogTitle,
Divider,
Drawer,
IconButton,
ListItemIcon,
ListItemText,
Menu,
MenuItem,
Switch,
TextField,
Tooltip,
Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import DescriptionIcon from "@mui/icons-material/Description";
import { deleteChat } from "../api/chat";
import { MessagesMap } from "../hooks/useChatMessages";
import {changePasswordRequest} from "../api/changePass";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
Sidebar,
Logo,
ChatList,
ChatListItem,
ChatTitle,
UserAvatarContainer,
AvatarImage,
DefaultAvatarIcon,
SidebarHandle,
NewChatButton
} from "./styled";
import { ChatSummary, DatabaseInfo } from "../types/chat";
import SettingsDrawer from "./Settings";
import SettingsModal from "./Settings";
import theme from "../theme/theme";
import { useUser } from "./UserContext";

// Dark theme colors
const DARK_COLORS = {
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  SUCCESS: "#4caf50",
  ERROR: "#ff4444",
  WARNING: "#ff9800",
  INFO: "#2196f3"
};

const TRANSLATIONS: Record<string, any> = {
  Italian: {
    welcome: "Benvenuto",
    newChat: "Nuova Chat",
    search: "Cerca...",
    today: "Oggi",
    yesterday: "Ieri",
    previous7Days: "Ultimi 7 giorni",
    previous30Days: "Ultimi 30 giorni",
    older: "Meno recenti",
    rename: "Rinomina",
    delete: "Elimina",
    cancel: "Annulla",
    save: "Salva",
    deleteChatTitle: "Elimina Chat",
    deleteChatConfirm: "Sei sicuro di voler eliminare questa chat?",
    settings: "Impostazioni",
    logout: "Esci",
    changePassword: "Cambia Password",
    profile: "Profilo",
    noChats: "Nessuna chat trovata"
  },
  French: {
    welcome: "Bienvenue",
    newChat: "Nouvelle discussion",
    search: "Rechercher...",
    today: "Aujourd'hui",
    yesterday: "Hier",
    previous7Days: "7 derniers jours",
    previous30Days: "30 derniers jours",
    older: "Plus ancien",
    rename: "Renommer",
    delete: "Supprimer",
    cancel: "Annuler",
    save: "Enregistrer",
    deleteChatTitle: "Supprimer la discussion",
    deleteChatConfirm: "Êtes-vous sûr de vouloir supprimer cette discussion ?",
    settings: "Paramètres",
    logout: "Se déconnecter",
    changePassword: "Changer le mot de passe",
    profile: "Profil",
    noChats: "Aucune discussion trouvée"
  }
};

interface SidebarProps {
username: string;
fullName: string;
avatar: string | null;
navigate: ReturnType<typeof useNavigate>;
email: string;
savedChats: ChatSummary[];
chatId: string;
editingChatId: string | null;
editTitle: string;
editingChatHandlers: {
setEditTitle: (v: string) => void;
handleRename: (id: string) => void;
setEditingChatId: (v: string | null) => void;
 };
menuAnchorEl: HTMLElement | null;
menuChatId: string | null;
menuChatTitle: string;
handleMenuOpen: (e: React.MouseEvent<HTMLElement>, c: ChatSummary) => void;
handleMenuClose: () => void;
handleMenuRename: () => void;
handleMenuDelete: () => void;
isCollapsed: boolean;
toggleSidebar: () => void;
availableDatabases: DatabaseInfo[];
dbMenuAnchorEl: HTMLElement | null;
handleDbMenuOpen: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement>) => void;
handleDbMenuClose: () => void;
handleSelectDatabase: (db: DatabaseInfo) => void;
settingsOpen: boolean;
handleSelectChat: (id: string) => void;
setSavedChats: React.Dispatch<React.SetStateAction<ChatSummary[]>>;
setMessagesMap: React.Dispatch<React.SetStateAction<MessagesMap>>;
avatarAnchorRef: React.RefObject<HTMLDivElement | null>;
showAvatarSettings: boolean;
setShowAvatarSettings: (v: boolean) => void;
setSettingsOpen: (v: boolean) => void;
onLogout: () => void;
}

interface SortableChatItemProps {
  chat: ChatSummary;
  isSelected: boolean;
  isEditing: boolean;
  editTitle: string;
  shortTitle: string;
  currentChatId: string;
  editingChatHandlers: {
    setEditTitle: (v: string) => void;
    handleRename: (id: string) => void;
    setEditingChatId: (v: string | null) => void;
  };
  onSelect: () => void;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>, chat: ChatSummary) => void;
  RenameInput: React.ComponentType<any>;
  t: any;
}

const SortableChatItem: React.FC<SortableChatItemProps> = ({
  chat,
  isSelected,
  isEditing,
  editTitle,
  shortTitle,
  currentChatId,
  editingChatHandlers,
  onSelect,
  onMenuOpen,
  RenameInput,
  t
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: chat.chatId,
    disabled: isEditing, 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };


  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
      sx={{ mb: 0.5, px: 0 }}
      {...attributes}
    >
      <ListItemButton
        selected={isSelected}
        onClick={onSelect}
        sx={{
          borderRadius: '8px',
          border: isSelected ? `1px solid ${DARK_COLORS.TEXT_PRIMARY}` : 'none',
          transition: 'all 0.2s',
          py: 0.8,
          px: 1.5,
          mx: 0.5,
          backgroundColor: isSelected ? DARK_COLORS.SECONDARY : 'transparent',
          '&:hover': {
            backgroundColor: DARK_COLORS.HOVER,
          },
        }}
      >
       <ListItemIcon 
          sx={{ 
            minWidth: 20, 
            mr: 1,
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' }
          }}
          {...listeners} 
        >
          <DragIndicatorIcon 
            sx={{ 
              fontSize: 16, 
              color: DARK_COLORS.TEXT_SECONDARY,
              opacity: 0.6,
              '&:hover': { opacity: 1 }
            }} 
          />
        </ListItemIcon>

        <ListItemText
          sx={{
            minWidth: 0,
            flex: 1,
            pr: 1,
          }}
          primary={
            isEditing ? (
              <RenameInput
                value={editTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => editingChatHandlers.setEditTitle(e.target.value)}
                onBlur={() => editingChatHandlers.handleRename(chat.chatId)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') editingChatHandlers.handleRename(chat.chatId);
                  if (e.key === 'Escape') editingChatHandlers.setEditingChatId(null);
                }}
                autoFocus
                placeholder={t.rename + "..."}
              />
            ) : (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: DARK_COLORS.TEXT_PRIMARY,
                  fontWeight: chat.chatId === currentChatId ? 600 : 400,
                  fontSize: '14px',
                }}
                title={chat.title}
              >
                <TypingTitle title={shortTitle} />
              </Box>
            )
          }
        />
        <IconButton
          size="small"
          aria-label="Apri il menu della chat"
          onClick={(e) => {
            e.stopPropagation();
            onMenuOpen(e, chat);
          }}
          sx={{
            color: DARK_COLORS.TEXT_SECONDARY,
            opacity: 0.6,
            '&:hover': {
              color: DARK_COLORS.TEXT_PRIMARY,
              opacity: 1,
            },
            transition: 'all 0.2s ease',
            flexShrink: 0,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </ListItemButton>
    </ListItem>
  );
};

const ChatSidebar: React.FC<SidebarProps> = ({
 email,
 username,
 fullName,
 avatar,
 isCollapsed,
 toggleSidebar,
 savedChats,
 chatId,
 editingChatId,
 editTitle,
 editingChatHandlers,
 menuAnchorEl,
 menuChatId,
 menuChatTitle,
 handleMenuOpen,
 handleMenuClose,
 handleMenuRename,
 handleMenuDelete,
 settingsOpen,
 availableDatabases,
 dbMenuAnchorEl,
 handleDbMenuOpen,
 handleDbMenuClose,
 handleSelectDatabase,
 handleSelectChat,
 avatarAnchorRef,
 showAvatarSettings,
 setShowAvatarSettings,
 setSettingsOpen,
 onLogout,
 setSavedChats,
 setMessagesMap,
 navigate,
}) => {
 const { language, user } = useUser();
 const t = TRANSLATIONS[language] || TRANSLATIONS["Italian"];
 const [changePwOpen, setChangePwOpen] = useState(false);
 const [searchTerm, setSearchTerm] = useState("");

 const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'super_admin';

 const sensors = useSensors(
   useSensor(PointerSensor),
   useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
 );

const handleDragEnd = (event: DragEndEvent) => {
  const { active, over } = event;
  if (active.id !== over?.id && over?.id) {
    const oldIndex = savedChats.findIndex((c) => c.chatId === active.id);
    const newIndex = savedChats.findIndex((c) => c.chatId === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(savedChats, oldIndex, newIndex);
      setSavedChats(reordered);
      
      const chatOrder = reordered.map(chat => chat.chatId);
      localStorage.setItem('chatOrder', JSON.stringify(chatOrder));
    }
  }
};
const handleOpenChangePw = () => setChangePwOpen(true);
const handleCloseChangePw = () => setChangePwOpen(false);

const splitTitle = (title: string) => {
 if (!title) return [t.newChat, ""];
 const parts = title.split("—");
 const main = parts[0]?.trim() || t.newChat;
 const db = parts[1]?.trim() || "";
 return [main, db];
};

const filteredChats = useMemo(() => {
 const term = searchTerm.trim().toLowerCase();
 return term
 ? savedChats.filter(chat =>
 chat.title.toLowerCase().includes(term)
 )
 : savedChats;
}, [searchTerm, savedChats]);

 const truncate = useCallback(
 (t: string) => (t.length > 20 ? t.slice(0, 20) + "…" : t),
 []
 );

 const RenameInput = styled(InputBase)(({ theme }) => ({
 width: "100%",
 fontSize: "15px",
 fontWeight: 500,
 color: DARK_COLORS.TEXT_PRIMARY,
 padding: "6px 12px",
 borderRadius: "12px",
 backgroundColor: DARK_COLORS.ACCENT,
 border: `1px solid ${DARK_COLORS.BORDER}`,
 boxShadow: `inset 0 0 0 1px ${DARK_COLORS.BORDER}`,
 transition: "all 0.18s ease",
 "&:hover": {
 backgroundColor: DARK_COLORS.HOVER,
 borderColor: DARK_COLORS.TEXT_SECONDARY,
 },
 "&.Mui-focused": {
 backgroundColor: DARK_COLORS.SECONDARY,
 borderColor: DARK_COLORS.TEXT_PRIMARY,
 boxShadow: `0 0 0 2px rgba(255, 255, 255, 0.2)`,
 },
 "& input": {
 padding: 0,
 color: DARK_COLORS.TEXT_PRIMARY,
 },
 }));

 return (
 <>
 <Sidebar collapsed={isCollapsed}>
 <Logo 
  src="/assisto-white-color.png" 
  alt="Logo" 
  style={{ 
    height: '45px',
    width: '200px',
  }}
/>

 <ChatTitle style={{ color: DARK_COLORS.TEXT_PRIMARY }}>
   {t.welcome}, {username}
 </ChatTitle>
 
 <Box
 sx={{
 width: "100%",
 mb: 1.5,
 display: "flex",
 justifyContent: "center",
 alignItems: "center",
 backgroundColor: "transparent",
 px: 1.5,
 }}
>
 <TextField
 fullWidth
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === "Enter" && !searchTerm.trim()) {
 handleDbMenuOpen(e);
 }
 }}
 placeholder={t.search}
 variant="outlined"
 size="small"
 sx={{
 "& .MuiOutlinedInput-root": {
 borderRadius: "20px",
 backgroundColor: DARK_COLORS.ACCENT,
 paddingRight: "4px",
 height: "40px",
 fontSize: "14px",
 transition: "all 0.2s ease",
 "&:hover": {
 backgroundColor: DARK_COLORS.HOVER,
 },
 "&.Mui-focused": {
 backgroundColor: DARK_COLORS.SECONDARY,
 },
 "& fieldset": {
 borderColor: "transparent",
 },
 "&:hover fieldset": {
 borderColor: DARK_COLORS.BORDER,
 },
 "&.Mui-focused fieldset": {
 borderColor: DARK_COLORS.TEXT_SECONDARY,
 },
 "& input": {
 color: DARK_COLORS.TEXT_PRIMARY,
 fontSize: "14px",
 padding: "8px 12px",
 },
 "& input::placeholder": {
 color: DARK_COLORS.TEXT_SECONDARY,
 opacity: 0.7,
 fontSize: "13px",
 },
},
 }}
 InputProps={{
 startAdornment: (
 <InputAdornment position="start">
 <SearchIcon sx={{ color: DARK_COLORS.TEXT_SECONDARY, fontSize: "18px" }} />
 </InputAdornment>
 ),
 endAdornment: (
 <InputAdornment position="end">
 <IconButton
 edge="end"
 size="small"
 onClick={handleDbMenuOpen}
 sx={{
 width: "28px",
 height: "28px",
 borderRadius: "8px",
 transition: "all 0.2s",
 "&:hover": {
 backgroundColor: DARK_COLORS.HOVER,
 },
 }}
 >
 <AddIcon sx={{ fontSize: "16px", color: DARK_COLORS.TEXT_SECONDARY }} />
 </IconButton>
 </InputAdornment>
 ),
 }}
 />
</Box>

 <ChatList style={{ overflowY: 'auto', overflowX: 'hidden' }}>
   <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
     <SortableContext items={filteredChats.map((c) => c.chatId)} strategy={verticalListSortingStrategy}>
       {filteredChats.map((chat) => {
         const [shortTitle] = splitTitle(chat.title);
         const isEditingLocal = editingChatId === chat.chatId;
         return (
           <SortableChatItem
             key={chat.chatId}
             chat={chat}
             isSelected={chat.chatId === chatId && !isEditingLocal}
             isEditing={isEditingLocal}
             editTitle={editTitle}
             shortTitle={shortTitle}
             currentChatId={chatId}
             editingChatHandlers={editingChatHandlers}
             onSelect={() => handleSelectChat(chat.chatId)}
             onMenuOpen={handleMenuOpen}
             RenameInput={RenameInput}
             t={t}
           />
         );
       })}
     </SortableContext>
   </DndContext>
 </ChatList>

 <Menu
 anchorEl={menuAnchorEl}
 open={Boolean(menuAnchorEl)}
 onClose={handleMenuClose}
 elevation={4}
 anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
 transformOrigin={{ vertical: "top", horizontal: "right" }}
 PaperProps={{
 sx: {
 borderRadius: 2.5,
 minWidth: 156,
 boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
 p: 0,
 bgcolor: DARK_COLORS.SURFACE,
 border: `1px solid ${DARK_COLORS.BORDER}`,
 "& .MuiMenuItem-root": {
 px: 2,
 py: 1,
 fontSize: 15.5,
 color: DARK_COLORS.TEXT_PRIMARY,
 "&:hover": { 
   bgcolor: DARK_COLORS.HOVER,
   transform: 'translateX(4px)',
 },
 transition: 'all 0.2s ease',
 },
 "& .MuiDivider-root": { 
   my: 0.4, 
   borderColor: DARK_COLORS.BORDER,
 },
 "& .MuiListItemIcon-root": { minWidth: 30 },
 },
 }}
 MenuListProps={{ dense: true }}
 >
 <MenuItem onClick={handleMenuRename}>
 <ListItemIcon>
   <EditIcon fontSize="small" sx={{ color: DARK_COLORS.TEXT_SECONDARY }} />
 </ListItemIcon>
 <ListItemText 
   primary={t.rename} 
   sx={{ "& .MuiTypography-root": { color: DARK_COLORS.TEXT_PRIMARY } }}
 />
 </MenuItem>
 <Divider sx={{ my: 0, borderColor: DARK_COLORS.BORDER }} />
 <MenuItem
 onClick={() => {
 const chatToDelete = savedChats.find((c) => c.chatId === menuChatId);
 if (!chatToDelete) return;
 if (!window.confirm(t.deleteChatConfirm)) return;
 deleteChat(chatToDelete.id)
 .then(() => {
 setSavedChats((chats) => chats.filter((c) => c.id !== chatToDelete.id));
 setMessagesMap((prev) => {
 const copy = { ...prev };
 delete copy[chatToDelete.chatId];
 return copy;
 });
 if (chatId === chatToDelete.chatId) {
 const rest = savedChats.filter((c) => c.id !== chatToDelete.id);
 navigate(rest.length ? `/chat/${rest[0].chatId}` : `/chat`, { replace: true });
 }
 handleMenuClose();
 })
 .catch((err) => alert("Error: " + err.message));
 }}
 sx={{
 color: DARK_COLORS.ERROR,
 fontWeight: 500,
 "& .MuiListItemIcon-root": { color: DARK_COLORS.ERROR },
 "&:hover": { 
   bgcolor: "rgba(255, 68, 68, 0.1)",
   transform: 'translateX(4px)',
 }
 }}
 >
 <ListItemIcon>
 <DeleteIcon fontSize="small" sx={{ color: DARK_COLORS.ERROR }} />
 </ListItemIcon>
 <ListItemText 
   primary={t.delete} 
   sx={{ "& .MuiTypography-root": { color: DARK_COLORS.ERROR } }}
 />
 </MenuItem>
 </Menu>

 <Menu
 id="db-select-menu"
 anchorEl={dbMenuAnchorEl}
 open={Boolean(dbMenuAnchorEl)}
 onClose={handleDbMenuClose}
 elevation={2}
 anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
 transformOrigin={{ vertical: "top", horizontal: "left" }}
 PaperProps={{
 sx: {
 borderRadius: 2,
 p: 0,
 minWidth: 160,
 boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
 bgcolor: DARK_COLORS.SURFACE,
 border: `1px solid ${DARK_COLORS.BORDER}`,
 "& .MuiMenuItem-root": {
 px: 1.5,
 py: 0.75,
 fontSize: 14.5,
 fontWeight: 500,
 color: DARK_COLORS.TEXT_PRIMARY,
 "&:hover": {
 bgcolor: DARK_COLORS.HOVER,
 transform: 'translateX(4px)',
 },
 transition: 'all 0.2s ease',
 },
 },
 }}
 MenuListProps={{ dense: true }}
 >
 {availableDatabases.map((db) => (
   <MenuItem
     key={(db as any).database_id ?? db.id}
     onClick={() => {
       handleSelectDatabase(db);
       handleDbMenuClose();
     }}
     sx={{
       color: `${DARK_COLORS.TEXT_PRIMARY} !important`,
       '& span': { color: `${DARK_COLORS.TEXT_PRIMARY} !important` }
     }}
   >
     <span>{(db as any).database_name ?? db.name}</span>
   </MenuItem>
 ))}
 </Menu>

 <UserAvatarContainer style={{ 
   marginTop: 'auto',
   padding: '12px',
   backgroundColor: 'transparent',
   border: 'none',
   borderRadius: 0,
 }}>
 <Box
 ref={avatarAnchorRef as React.RefObject<HTMLDivElement>}
 onClick={() => setShowAvatarSettings(true)}
 sx={{
 display: "flex",
 alignItems: "center",
 gap: 1.5,
 width: "100%",
 px: 1.5,
 py: 1.2,
 borderRadius: "12px",
 cursor: "pointer",
 backgroundColor: "transparent",
 transition: "all 0.2s ease",
 "&:hover": {
 backgroundColor: DARK_COLORS.HOVER,
 },
 }}
 >
 {avatar ? (
 <Box
   component="img"
   src={avatar}
   sx={{
     width: 32,
     height: 32,
     borderRadius: '50%',
     objectFit: 'cover',
   }}
 />
 ) : (
 <Box
   sx={{
     width: 32,
     height: 32,
     borderRadius: '50%',
     backgroundColor: DARK_COLORS.ACCENT,
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
   }}
 >
   <DefaultAvatarIcon sx={{ 
     fontSize: 20, 
     color: DARK_COLORS.TEXT_PRIMARY,
   }} />
 </Box>
 )}
 <Typography
 variant="body2"
 sx={{
 fontSize: "14px",
 fontWeight: 500,
 color: DARK_COLORS.TEXT_PRIMARY,
 whiteSpace: 'nowrap',
 overflow: 'hidden',
 textOverflow: 'ellipsis',
 }}
 >
 {t.profile}
 </Typography>
 </Box>

 <Menu
 anchorEl={avatarAnchorRef.current}
 open={showAvatarSettings}
 onClose={() => setShowAvatarSettings(false)}
 elevation={4}
 anchorOrigin={{ vertical: "top", horizontal: "right" }}
 transformOrigin={{ vertical: "bottom", horizontal: "right" }}
 PaperProps={{
 sx: {
 borderRadius: 2.5,
 minWidth: 156,
 boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
 p: 0,
 bgcolor: DARK_COLORS.SURFACE,
 border: `1px solid ${DARK_COLORS.BORDER}`,
 "& .MuiMenuItem-root": {
 px: 2,
 py: 1,
 fontSize: 15.5,
 color: DARK_COLORS.TEXT_PRIMARY,
 "&:hover": { 
   bgcolor: DARK_COLORS.HOVER,
   transform: 'translateX(4px)',
 },
 transition: 'all 0.2s ease',
 },
 "& .MuiDivider-root": { 
   my: 0.4, 
   borderColor: DARK_COLORS.BORDER,
 },
 "& .MuiListItemIcon-root": { 
   minWidth: 30,
   color: DARK_COLORS.TEXT_SECONDARY,
 },
 "& .MuiListItemText-primary": {
   color: DARK_COLORS.TEXT_PRIMARY,
 },
 },
 }}
 MenuListProps={{ dense: true }}
 >
 <Box sx={{ 
   px: 2, 
   py: 1, 
   fontWeight: 600, 
   color: DARK_COLORS.TEXT_PRIMARY, 
   fontSize: 15.5,
   borderBottom: `1px solid ${DARK_COLORS.BORDER}`,
   backgroundColor: DARK_COLORS.ACCENT,
 }}>
 {(username || '').split(' ')[0]}
 </Box>
 <Divider sx={{ my: 0, borderColor: DARK_COLORS.BORDER }} />
 <MenuItem
 onClick={() => {
 setSettingsOpen(true);
 setShowAvatarSettings(false);
 }}
 sx={{
   color: DARK_COLORS.TEXT_PRIMARY,
   "& .MuiListItemIcon-root": { color: DARK_COLORS.TEXT_SECONDARY },
   "& .MuiListItemText-primary": { color: DARK_COLORS.TEXT_PRIMARY },
 }}
 >
 <ListItemIcon>
   <SettingsIcon fontSize="small" sx={{ color: DARK_COLORS.TEXT_SECONDARY }} />
 </ListItemIcon>
 <ListItemText 
   primary={t.settings} 
   sx={{ "& .MuiTypography-root": { color: DARK_COLORS.TEXT_PRIMARY } }}
 />
 </MenuItem>
 {isAdmin && (
   <MenuItem
     onClick={() => {
       navigate("/releases");
       setShowAvatarSettings(false);
     }}
     sx={{
       color: DARK_COLORS.TEXT_PRIMARY,
       "& .MuiListItemIcon-root": { color: DARK_COLORS.TEXT_SECONDARY },
       "& .MuiListItemText-primary": { color: DARK_COLORS.TEXT_PRIMARY },
     }}
   >
     <ListItemIcon>
       <DescriptionIcon fontSize="small" sx={{ color: DARK_COLORS.TEXT_SECONDARY }} />
     </ListItemIcon>
     <ListItemText 
       primary="Release Notes" 
       sx={{ "& .MuiTypography-root": { color: DARK_COLORS.TEXT_PRIMARY } }}
     />
   </MenuItem>
 )}
 <MenuItem
 onClick={onLogout}
 sx={{
 color: DARK_COLORS.ERROR,
 "& .MuiListItemIcon-root": { color: DARK_COLORS.ERROR },
 "& .MuiListItemText-primary": { color: DARK_COLORS.ERROR },
 fontWeight: 500,
 "&:hover": { 
   bgcolor: "rgba(255, 68, 68, 0.1)",
   transform: 'translateX(4px)',
 }
 }}
 >
 <ListItemIcon>
   <LogoutIcon fontSize="small" sx={{ color: DARK_COLORS.ERROR }} />
 </ListItemIcon>
 <ListItemText 
   primary={t.logout} 
   sx={{ "& .MuiTypography-root": { color: DARK_COLORS.ERROR } }}
 />
 </MenuItem>
 </Menu>
 </UserAvatarContainer>

 <SettingsModal
 open={settingsOpen}
 onClose={() => setSettingsOpen(false)}
 fullName={fullName}
 username={username}
 email={email}
 onLogout={onLogout}
 onOpenChangePw={handleOpenChangePw}
 />
 </Sidebar>

 <Dialog
 open={changePwOpen}
 onClose={handleCloseChangePw}
 fullWidth
 maxWidth="xs"
 PaperProps={{
 sx: {
 borderRadius: 4,
 boxShadow: "0 12px 48px rgba(0, 0, 0, 0.8)",
 background: DARK_COLORS.SURFACE,
 },
 }}
 >

 <DialogContent sx={{ p: 0 }}>
 <ChangePassword onSuccess={handleCloseChangePw} />
 </DialogContent>
 </Dialog>
 </>
 );
};

export default ChatSidebar;
